import { useMemo } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { ParcelCompanyModel } from "../../model/types";
import { ParcelCompaniesResponseTypesDTO } from "../response.types";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { parcelCarriersService } from "../parcelCarriers.api";

type ResetParcelCarriersQueryFunc = () => Promise<void>;

export type ParcelCompanyOption = {
  name: string;
  scacCode: string;
};

export type UseParcelCarriersQueryReturn = {
  getParcelCarriers: () => void;
  resetParcelCarriersQuery: ResetParcelCarriersQueryFunc;
  parcelCompanies: ParcelCompanyModel[];
  parcelCompanyOptions: ParcelCompanyOption[];
  isParcelCarriersSuccess: boolean;
  isParcelCarriersError: boolean;
  isParcelCarriersLoading: boolean;
};

type UseParcelCarriersQueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  requestOnMount?: boolean;
};

const PARCEL_CARRIERS_QUERY_KEY = "get_parcel_carriers_query";

export const useParcelCarriersQuery = ({
  requestOnMount,
}: UseParcelCarriersQueryParams = {}): UseParcelCarriersQueryReturn => {
  const queryClient = useQueryClient();

  const { data, isError, isLoading, isSuccess } = useQuery<
    ParcelCompaniesResponseTypesDTO,
    Error,
    ParcelCompanyModel[]
  >({
    queryKey: [PARCEL_CARRIERS_QUERY_KEY],
    queryFn: parcelCarriersService.getParcelCarriers,
    select: (response) =>
      response?.data?.map((parcelCompany) => ({
        ...parcelCompany,
        countryCode: parcelCompany.country_code,
        carrierCode: parcelCompany.carrier_code,
      })) ?? [],
    enabled: requestOnMount,
  });

  const resetParcelCarriersQuery: ResetParcelCarriersQueryFunc = () => {
    return queryClient.resetQueries({ queryKey: [PARCEL_CARRIERS_QUERY_KEY] });
  };

  const getParcelCarriers = () => {
    queryClient.invalidateQueries({ queryKey: [PARCEL_CARRIERS_QUERY_KEY] });
  };

  const parcelCompaniesMappedToOptions = useMemo<ParcelCompanyOption[]>(() => {
    return (
      data?.map((parcelCompany) => ({
        name: parcelCompany.name,
        scacCode: parcelCompany.carrierCode,
      })) ?? []
    );
  }, [data]);

  return {
    resetParcelCarriersQuery,
    getParcelCarriers,
    parcelCompanies: data ?? [],
    parcelCompanyOptions: parcelCompaniesMappedToOptions,
    isParcelCarriersSuccess: isSuccess,
    isParcelCarriersError: isError,
    isParcelCarriersLoading: isLoading,
  };
};
